package com.evoapps.evoshift.domain.model

import kotlinx.datetime.LocalDate

/**
 * A stretch of local time, [start] inclusive to [end] exclusive, in minutes since 1970-01-01
 * 00:00 (local wall-clock time, no time zone: shifts are planned in the workplace's local time).
 */
data class TimeWindow(val start: Long, val end: Long) {
    /** The day [start] falls on. */
    val startDate: LocalDate get() = LocalDate.fromEpochDays(start.floorDiv(MINUTES_PER_DAY))

    /** Minutes after midnight of [start] and of [end] (0..1439); an [end] at midnight is 0. */
    val startMinuteOfDay: Int get() = start.mod(MINUTES_PER_DAY).toInt()
    val endMinuteOfDay: Int get() = end.mod(MINUTES_PER_DAY).toInt()

    companion object {
        const val MINUTES_PER_DAY = 1440L
    }
}

/** Someone ([userId]) working from [start] to [end] (see [TimeWindow]), e.g. a shift ([shiftId]). */
data class WorkSpan(val userId: String, val start: Long, val end: Long, val shiftId: String? = null) {
    fun overlaps(window: TimeWindow): Boolean = start < window.end && end > window.start
}

/**
 * SH-104: people who need supervision are never alone at the same time: at every moment one of
 * them works, someone who does not need supervision works too (the owner's rule 7). The
 * auto-scheduler (AutoScheduler's supervision check) and the roster's day warnings
 * ([DayStaffing]) both use [uncoveredWindows], so the two agree.
 */
object SupervisionCheck {

    /**
     * The times, merged and in order, when somebody works and everyone working needs supervision
     * ([needsSupervision] by user id). Spans may run past midnight (their [WorkSpan.end] is simply
     * on the next day); empty or inverted spans are ignored. Nobody working: no window.
     */
    fun uncoveredWindows(spans: List<WorkSpan>, needsSupervision: (String) -> Boolean): List<TimeWindow> {
        val valid = spans.filter { it.end > it.start }
        if (valid.isEmpty()) return emptyList()
        val supervised = valid.map { needsSupervision(it.userId) }
        if (supervised.none { it }) return emptyList()
        val points = valid.flatMap { listOf(it.start, it.end) }.distinct().sorted()
        val windows = ArrayList<TimeWindow>()
        for (i in 0 until points.size - 1) {
            val a = points[i]
            val b = points[i + 1]
            var anyone = false
            var covered = false
            for (k in valid.indices) {
                val span = valid[k]
                if (span.start > a || span.end < b) continue
                anyone = true
                if (!supervised[k]) { covered = true; break }
            }
            if (!anyone || covered) continue
            val last = windows.lastOrNull()
            if (last != null && last.end == a) windows[windows.size - 1] = TimeWindow(last.start, b)
            else windows += TimeWindow(a, b)
        }
        return windows
    }

    /**
     * The planned times of [shifts] that have a person, as spans. A shift whose end is not after
     * its start is read as ending on the next day (older documents stored the start's date); an
     * unreadable time is skipped.
     */
    fun spans(shifts: Collection<Shift>): List<WorkSpan> = shifts.mapNotNull { shift ->
        val userId = shift.userId ?: return@mapNotNull null
        val start = localMinutes(shift.plannedTime.start) ?: return@mapNotNull null
        var end = localMinutes(shift.plannedTime.end) ?: return@mapNotNull null
        if (end <= start) end += TimeWindow.MINUTES_PER_DAY
        if (end <= start) return@mapNotNull null
        WorkSpan(userId, start, end, shift.id)
    }

    /** Minutes since the epoch of a local "YYYY-MM-DDTHH:mm[:ss]" timestamp; null when unreadable. */
    fun localMinutes(iso: String): Long? = runCatching {
        val day = LocalDate.parse(iso.substring(0, 10)).toEpochDays()
        val clock = iso.substring(11, 16)
        day * TimeWindow.MINUTES_PER_DAY + clock.substring(0, 2).toInt() * 60 + clock.substring(3, 5).toInt()
    }.getOrNull()
}
